import React, { useEffect } from "react";

import {
  mdiApplicationCogOutline,
  mdiArrowDecisionOutline,
  mdiArrowLeft,
  mdiCalendarMonthOutline,
  mdiCalendarPlus,
  mdiCashMultiple,
  mdiChartGantt,
  mdiChartTimeline,
  mdiFormatListGroupPlus,
  mdiTableCog,
  mdiTimelineCheck,
  mdiTune,
  mdiViewGridPlus,
} from "@mdi/js";
import Icon from "@mdi/react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useParams } from "react-router-dom";

import MainMenu from "./MainMenu";
import Menu, { MENU_ICON_SIZE } from "./Menu";
import MenuItem from "./MenuItem";
import Sidebar from "./Sidebar";
import SubMenu from "./SubMenu";
import FastAPI from "../../api/FastAPI";
import { FAST_2_0 } from "../../constants/fast";
import useFAST from "../../contexts/FASTContext";
import i18n from "../../i18n";
import { getTimeboundsQueryString } from "../../utils/fast";
import { FAST_CHANNELS_ROUTE } from "../routes/routes";
import AbilityContainer from "../utils/abilities/AbilityContainer";

import type { JSX } from "react";

import type { FASTChannelRoutesParams } from "../routes/FASTChannelRoutes";

function FastMenu(): JSX.Element | null {
  const { t } = useTranslation("editor-menu");
  const { dispatch } = useFAST();

  const params = useParams<FASTChannelRoutesParams>();

  const timeboundsQuery = getTimeboundsQueryString(
    moment().startOf("hour").subtract(30, "minutes").toDate(),
  );

  const { channelId } = params;

  const { language } = i18n;
  const getChannelQuery = FastAPI.generateQuery({
    fn: FastAPI.getChannel,
    params: [channelId],
  });

  const { data: channelData } = useQuery(getChannelQuery);

  moment.locale(language);

  useEffect(() => {
    if (channelData) {
      dispatch({
        type: "GET_CHANNEL",
        data: channelData,
      });
    }
  }, [channelData, dispatch]);

  const isLegacyFASTChannel = channelData?.version !== FAST_2_0;

  if (isLegacyFASTChannel) {
    return <MainMenu />;
  }
  return (
    <Sidebar isCollapsable id="sidebar-fast-menu" title={channelData?.name}>
      <Menu>
        <MenuItem
          className="uppercase"
          component={<Link to={FAST_CHANNELS_ROUTE} />}
          icon={<Icon path={mdiArrowLeft} size={MENU_ICON_SIZE} />}
          label={t("backChannels")}
        />
        <AbilityContainer abilityList={["fastChannelsWrite"]}>
          <SubMenu
            isDefaultOpen
            icon={<Icon path={mdiViewGridPlus} size={MENU_ICON_SIZE} />}
            label={t("prepare")}
          >
            <MenuItem
              label={t("lists")}
              component={
                <NavLink
                  to={`${FAST_CHANNELS_ROUTE}/${channelId}/fast-playlists`}
                />
              }
              icon={
                <Icon path={mdiFormatListGroupPlus} size={MENU_ICON_SIZE} />
              }
            />
            <MenuItem
              icon={<Icon path={mdiCashMultiple} size={MENU_ICON_SIZE} />}
              label={t("ad_breaks")}
              component={
                <NavLink to={`${FAST_CHANNELS_ROUTE}/${channelId}/ad-breaks`} />
              }
            />
            <MenuItem
              icon={<Icon path={mdiTimelineCheck} size={MENU_ICON_SIZE} />}
              label={t("timeslots")}
              component={
                <NavLink to={`${FAST_CHANNELS_ROUTE}/${channelId}/timeslots`} />
              }
            />
          </SubMenu>
        </AbilityContainer>
        <SubMenu
          isDefaultOpen
          icon={<Icon path={mdiCalendarPlus} size={MENU_ICON_SIZE} />}
          label={t("schedule")}
        >
          <MenuItem
            abilityList={["fastChannelsWrite"]}
            icon={<Icon path={mdiChartTimeline} size={MENU_ICON_SIZE} />}
            label={t("templating")}
            component={
              <NavLink to={`${FAST_CHANNELS_ROUTE}/${channelId}/templating`} />
            }
          />

          <MenuItem
            icon={<Icon path={mdiCalendarMonthOutline} size={MENU_ICON_SIZE} />}
            label={t("calendar")}
            component={
              <NavLink
                exact
                to={`${FAST_CHANNELS_ROUTE}/${channelId}/calendar`}
              />
            }
          />
          <MenuItem
            icon={<Icon path={mdiChartGantt} size={MENU_ICON_SIZE} />}
            label={t("scheduling")}
            component={
              <NavLink
                to={`${FAST_CHANNELS_ROUTE}/${channelId}/scheduling${timeboundsQuery}`}
              />
            }
          />
        </SubMenu>
        <MenuItem
          className="uppercase"
          icon={<Icon path={mdiArrowDecisionOutline} size={MENU_ICON_SIZE} />}
          label={t("distribute")}
          component={
            <NavLink
              exact
              to={`${FAST_CHANNELS_ROUTE}/${channelId}/distribute`}
            />
          }
        />
        <AbilityContainer abilityList={["fastChannelsWrite"]}>
          <SubMenu
            isDefaultOpen
            icon={<Icon path={mdiTune} size={MENU_ICON_SIZE} />}
            label={t("configure")}
          >
            <MenuItem
              icon={<Icon path={mdiTableCog} size={MENU_ICON_SIZE} />}
              label={t("scheduling_settings")}
              component={
                <NavLink
                  exact
                  to={`${FAST_CHANNELS_ROUTE}/${channelId}/schedule-settings`}
                />
              }
            />
            <MenuItem
              label={t("channel_settings")}
              component={
                <NavLink
                  exact
                  to={`${FAST_CHANNELS_ROUTE}/${channelId}/channel-settings`}
                />
              }
              icon={
                <Icon path={mdiApplicationCogOutline} size={MENU_ICON_SIZE} />
              }
            />
          </SubMenu>
        </AbilityContainer>
      </Menu>
    </Sidebar>
  );
}

export default FastMenu;
